import React, { useEffect, useState } from 'react';
import type { JSX } from 'react';
import QRCode from 'qrcode';
import {
    AlertTriangle, ArrowLeft, CheckCircle2, Download, Fingerprint, Globe, Info, Loader2, Lock,
    QrCode, ShieldAlert, Smartphone, Tablet,
} from 'lucide-react';
import { appVariante, istAndroidBrowser, istIosGeraet, istNativeApp, type AppVariante } from '../../services/nativePlatform';
import {
    alsFingerabdruck, apkDownloadLink, appDownloadSeitenUrl, formatiereDatum, formatiereGroesse,
    geraeteVorschlag, ladeAlleAppReleases, ladeDownloadZugang, VARIANTEN_TEXT,
    type AppRelease, type DownloadZugang,
} from '../../services/appRelease';
import { useAppUpdate, type InstallZustand } from '../../services/appUpdate';
import { apkUpdateVerfuegbar } from '../../services/apkUpdate';
import { SensorMessmodus } from './SensorMessmodus';
import { VerortungGeraetKarte } from './VerortungGeraetKarte';
import { BiometrieKarte } from './BiometrieKarte';

/**
 * Seite `/#/app` der optionalen Android-App (docs/android-app-plan.md
 * Abschnitt 4, Vertrag E11–E13). Ziel des QR-Codes, des Menueeintrags und des
 * Hinweises im Android-Browser.
 *
 * Vier Gesichter, je nach Geraet:
 *   • in der App      → „App-Info": NUR die eigene Variante, installierte
 *                       Version und Update-Stand — statt Download-Werbung
 *                       (kein Push, Vertrag E20);
 *   • iPhone/iPad     → „Zum Home-Bildschirm hinzufuegen" (Apple erlaubt
 *                       keine APK);
 *   • sonst (Android-Browser, Rechner) → BEIDE Varianten: „Handy-App"
 *                       (Begehungen, Dashboard, Mitteilungen) und
 *                       „Tablet-App" (voller Umfang wie am PC), je mit
 *                       Knopf „herunterladen" (signierter Link, 10 min);
 *                       dazu Anleitung, Pruefsummen, QR-Code (nicht auf dem
 *                       Android-Geraet selbst). Auf einem Android-Geraet
 *                       steht die passende Variante vorn (kuerzere
 *                       Bildschirmseite ab 600 CSS-px → Tablet);
 *   • Freelancer      → „nicht verfuegbar" (E3; der Eimer ist fuer sie
 *                       ohnehin gesperrt).
 * Ohne veroeffentlichte Version: „Noch keine App-Version veroeffentlicht".
 *
 * Bewusst KEINE `fixed inset-0`-Flaeche: die Seite steht an der Stelle der
 * Ansichten, und die Android-Zurueck-Taste haelt solche Flaechen fuer Dialoge.
 */

type Plattform = 'app' | 'ios' | 'browser';

const plattformErkennen = (): Plattform => {
    if (istNativeApp()) return 'app';
    if (istIosGeraet()) return 'ios';
    return 'browser';
};

interface Stand {
    geladen: boolean;
    zugang: DownloadZugang | null;
    releases: Record<AppVariante, AppRelease | null>;
}

const KEINE_RELEASES: Record<AppVariante, AppRelease | null> = { handy: null, tablet: null };

/**
 * Download ueber einen unsichtbaren Anker mit `download`-Attribut statt
 * `location.assign` (Review R2): die Seite navigiert nicht selbst weg, und
 * kein neuer Tab (den der Popup-Blocker nach dem `await` schluckte).
 *
 * Nur im Browser: die Download-Knoepfe erscheinen ausschliesslich dort. In
 * der App zeigt die Seite die eigene Variante, und „Aktualisieren" geht ueber
 * `oeffneDownload` → Update in der App (Vertrag E21, Plugin `ApkUpdate`);
 * ohne Plugin (aeltere Huelle) bzw. als Rueckfall bei Fehler `externOeffnen`
 * (System-Browser).
 *
 * Grenze: der signierte Link liegt auf der Storage-Domain, also fremdem
 * Ursprung — dort werten Browser `download` nicht aus. Heruntergeladen wird
 * trotzdem, weil der Link `Content-Disposition: attachment` traegt
 * (`createSignedUrl(…, { download })`), und der Tab bleibt stehen. Nur eine
 * FEHLER-Antwort (Link abgelaufen) wuerde angezeigt statt geladen; der Link
 * ist beim Klick aber frisch (600 s) und die Datei gerade eben geprueft.
 */
const apkHerunterladen = (url: string, dateiname: string): void => {
    const anker = document.createElement('a');
    anker.href = url;
    anker.download = dateiname;
    anker.rel = 'noopener';
    anker.style.display = 'none';
    document.body.appendChild(anker);
    try {
        anker.click();
    } finally {
        anker.remove();
    }
};

const VariantenSymbol: React.FC<{ variante: AppVariante; className?: string }> = ({ variante, className }) =>
    variante === 'tablet' ? <Tablet className={className} /> : <Smartphone className={className} />;

const Karte: React.FC<{ titel?: string; symbol?: React.ReactNode; children: React.ReactNode }> = ({ titel, symbol, children }) => (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        {titel && (
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
                {symbol}
                {titel}
            </h2>
        )}
        {children}
    </section>
);

const Schritt: React.FC<{ nr: number; titel: string; children: React.ReactNode }> = ({ nr, titel, children }) => (
    <li className="flex gap-3">
        <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center">{nr}</span>
        <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800">{titel}</p>
            <p className="text-sm text-slate-600 leading-relaxed mt-0.5">{children}</p>
        </div>
    </li>
);

/** In der App: installierte Version und Update-Stand — nur die eigene Variante. */
/** Kurztext der In-App-Update-Stufe (ausführlich mit Knöpfen: AppUpdateBanner). */
const installationsText = (installation: InstallZustand): string => {
    switch (installation.phase) {
        case 'laden': return installation.prozent === null ? 'Wird geladen …' : `Wird geladen … ${installation.prozent} %`;
        case 'pruefen': return 'Wird geprüft …';
        case 'bereit': return 'Update bereit — die App wird beim Installieren geschlossen. Eingaben werden vorher gesichert.';
        case 'sichern': return 'Eingaben werden gesichert …';
        case 'bestaetigen': return 'Bitte Installation im Android-Dialog bestätigen. Die App schließt sich danach und muss neu geöffnet werden.';
        case 'installieren': return 'Wird installiert … Die App schließt sich dabei und muss danach neu geöffnet werden.';
        case 'erlaubnis_noetig': return 'Einmalig erlauben: in der Einstellung „Apps aus dieser Quelle zulassen" einschalten und zurück in die App wechseln.';
        case 'fehler':
            if (installation.nurInstallation) return installation.meldung ?? 'Sichern nicht bestätigt — bitte erneut versuchen.';
            return `Update fehlgeschlagen${installation.meldung ? `: ${installation.meldung}` : '.'}`;
        case 'fertig': return 'Update installiert — bitte die App neu öffnen.';
        default: return '';
    }
};

export const InstalliertKarte: React.FC<{ update: ReturnType<typeof useAppUpdate>; variante: AppVariante | null }> = ({ update, variante }) => {
    const { status, release, installiert, oeffneDownload, installation, jetztInstallieren, abbrechen, imBrowserLaden } = update;
    // Ruhe, Bereit, Erlaubnis und Fehler haben einen Knopf; die übrigen Stufen laufen (Spinner).
    const mitKnopf = installation.phase === 'ruhe' || installation.phase === 'bereit'
        || installation.phase === 'erlaubnis_noetig' || installation.phase === 'fehler';
    const [inApp] = useState<boolean>(() => apkUpdateVerfuegbar());
    const text = variante ? VARIANTEN_TEXT[variante] : null;
    return (
        <Karte
            titel={text ? `Diese App: ${text.titel}` : 'Diese App'}
            symbol={<VariantenSymbol variante={variante ?? 'handy'} className="w-4 h-4 text-emerald-600" />}
        >
            {text && <p className="text-xs text-slate-500 mb-3">{text.umfang}</p>}
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-slate-500">Installierte Version</dt>
                <dd className="font-semibold text-slate-800">
                    {installiert ? `${installiert.versionName} (Build ${installiert.versionCode})` : 'unbekannt'}
                </dd>
                {release && (
                    <>
                        <dt className="text-slate-500">Neueste Version</dt>
                        <dd className="text-slate-800">
                            {release.versionName} (Build {release.versionCode}) vom {formatiereDatum(release.date)}
                        </dd>
                    </>
                )}
            </dl>

            {status === 'aktuell' && (
                <p className="mt-4 text-sm text-emerald-700 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    {release ? 'Die App ist auf dem neuesten Stand.' : 'Keine neuere Version bekannt.'}
                </p>
            )}
            {status !== 'aktuell' && release && (
                <div className={`mt-4 rounded-xl border p-3 text-sm ${status === 'pflicht' ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                    <p className="font-semibold flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        {status === 'pflicht'
                            ? `Diese Version wird nicht mehr unterstützt — bitte auf ${release.versionName} aktualisieren.`
                            : `Neue App-Version ${release.versionName} verfügbar.`}
                    </p>
                    {release.notes && <p className="mt-1 whitespace-pre-line">{release.notes}</p>}
                    {mitKnopf ? (
                        <>
                            {installation.phase !== 'ruhe' && (
                                <p className="mt-3 text-xs font-semibold" role="status">
                                    {installationsText(installation)}
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={installation.phase === 'bereit' || installation.nurInstallation ? jetztInstallieren : oeffneDownload}
                                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-sm font-semibold px-4 py-3 transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                {installation.phase === 'bereit' ? 'Jetzt installieren'
                                    : installation.phase === 'erlaubnis_noetig' ? 'Einstellung öffnen'
                                        : installation.phase === 'fehler' ? 'Erneut versuchen' : 'Aktualisieren'}
                            </button>
                            {installation.phase === 'fehler' && !installation.nurInstallation && (
                                <button
                                    type="button"
                                    onClick={imBrowserLaden}
                                    className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white/70 hover:bg-white text-sm font-semibold px-4 py-2.5 transition-colors"
                                >
                                    <Globe className="w-4 h-4" />
                                    Im Browser laden
                                </button>
                            )}
                        </>
                    ) : (
                        <div className="mt-3" role="status" aria-live="polite">
                            <p className="text-xs font-semibold flex items-center gap-2">
                                {installation.phase === 'fertig'
                                    ? <CheckCircle2 className="w-4 h-4 shrink-0" />
                                    : <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
                                {installationsText(installation)}
                            </p>
                            {installation.phase === 'laden' && installation.prozent !== null && (
                                <div
                                    role="progressbar"
                                    aria-label="Download der neuen Version"
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-valuenow={installation.prozent}
                                    className="mt-2 h-2 w-full rounded-full bg-white/70 overflow-hidden"
                                >
                                    <div className="h-full bg-emerald-600 transition-[width] duration-300" style={{ width: `${installation.prozent}%` }} />
                                </div>
                            )}
                            {installation.phase === 'laden' && (
                                <button
                                    type="button"
                                    onClick={abbrechen}
                                    className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white/70 hover:bg-white text-sm font-semibold px-4 py-2.5 transition-colors"
                                >
                                    Abbrechen
                                </button>
                            )}
                        </div>
                    )}
                    <p className="mt-2 text-xs opacity-80">
                        {inApp
                            ? 'Die neue Version wird in der App geladen und geprüft; installiert wird erst auf „Jetzt installieren". Beim ersten Mal fragt Android einmalig, ob Apps aus dieser Quelle zugelassen werden. Anmeldung und Daten bleiben erhalten; Details zum Fortschritt zeigt auch die Leiste oben.'
                            : 'Der Download öffnet sich im Browser. Android installiert die neue Version über die alte — Anmeldung und Daten bleiben erhalten.'}
                    </p>
                </div>
            )}
            <p className="mt-4 text-xs text-slate-500">
                Neue Versionen meldet die App von selbst, beim Start und beim Zurückkehren.
            </p>
        </Karte>
    );
};

const IosKarte: React.FC = () => (
    <Karte titel="iPhone und iPad" symbol={<Info className="w-4 h-4 text-blue-600" />}>
        <p className="text-sm text-slate-600 leading-relaxed">
            Für iPhone und iPad gibt es keine App zum Herunterladen — Apple erlaubt die Installation nur über
            den App Store. Legen Sie die Web-App stattdessen auf den Home-Bildschirm, dann startet sie wie eine App:
        </p>
        <ol className="mt-4 space-y-3">
            <Schritt nr={1} titel="In Safari öffnen">Diese Seite in Safari aufrufen und anmelden.</Schritt>
            <Schritt nr={2} titel="Teilen">Unten (iPad: oben) auf das Teilen-Symbol tippen — das Quadrat mit dem Pfeil nach oben.</Schritt>
            <Schritt nr={3} titel="Zum Home-Bildschirm">In der Liste „Zum Home-Bildschirm“ wählen (eventuell etwas nach unten wischen).</Schritt>
            <Schritt nr={4} titel="Hinzufügen">Oben rechts „Hinzufügen“ tippen. Das Symbol liegt jetzt auf dem Home-Bildschirm.</Schritt>
        </ol>
    </Karte>
);

/** Eine Variante im Browser: Umfang, Version, Knopf. */
const VariantenKarte: React.FC<{
    variante: AppVariante;
    release: AppRelease | null;
    empfohlen: boolean;
    laedt: boolean;
    fehler: string | null;
    onHerunterladen: () => void;
}> = ({ variante, release, empfohlen, laedt, fehler, onHerunterladen }) => {
    const text = VARIANTEN_TEXT[variante];
    return (
        <section className={`bg-white rounded-2xl border shadow-sm p-5 flex flex-col ${empfohlen ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-slate-200'}`}>
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-emerald-50 border border-emerald-100 shrink-0">
                    <VariantenSymbol variante={variante} className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="min-w-0">
                    <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2 flex-wrap">
                        {text.titel}
                        {empfohlen && (
                            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
                                Passt zu diesem Gerät
                            </span>
                        )}
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">{text.umfang}</p>
                </div>
            </div>

            {release ? (
                <>
                    <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                        <dt className="text-slate-500">Version</dt>
                        <dd className="text-slate-800">{release.versionName} (Build {release.versionCode})</dd>
                        <dt className="text-slate-500">Veröffentlicht</dt>
                        <dd className="text-slate-800">{formatiereDatum(release.date)}</dd>
                        <dt className="text-slate-500">Größe</dt>
                        <dd className="text-slate-800">{formatiereGroesse(release.size)}</dd>
                    </dl>
                    {release.notes && (
                        <p className="mt-3 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{release.notes}</p>
                    )}
                    <div className="mt-auto pt-4">
                        <button
                            type="button"
                            onClick={onHerunterladen}
                            disabled={laedt}
                            className={`w-full inline-flex items-center justify-center gap-2 rounded-xl text-sm font-semibold px-4 py-3 transition-colors disabled:opacity-60 ${empfohlen ? 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white' : 'border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-700'}`}
                        >
                            {laedt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            {text.titel} herunterladen
                        </button>
                        {fehler && (
                            <p className="mt-2 text-sm text-rose-700 flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {fehler}
                            </p>
                        )}
                    </div>
                </>
            ) : (
                <p className="mt-3 text-sm text-slate-500">Noch nicht veröffentlicht.</p>
            )}
        </section>
    );
};

export function AppDownloadView({ onBack }: { onBack?: () => void }): JSX.Element {
    const [plattform] = useState<Plattform>(plattformErkennen);
    const [aufAndroidGeraet] = useState<boolean>(() => istAndroidBrowser());
    const [eigeneVariante] = useState<AppVariante | null>(() => appVariante());
    // Der Bildschirm-Vorschlag gilt nur auf dem Android-Geraet selbst: am
    // Rechner beschreibt der Monitor nicht das Geraet, auf dem die App landet.
    const [vorschlag] = useState<AppVariante | null>(() => (istAndroidBrowser() ? geraeteVorschlag() : null));
    const update = useAppUpdate();
    const [stand, setStand] = useState<Stand>({ geladen: false, zugang: null, releases: KEINE_RELEASES });
    const [qr, setQr] = useState<string | null>(null);
    const [laedtVariante, setLaedtVariante] = useState<AppVariante | null>(null);
    const [fehler, setFehler] = useState<{ variante: AppVariante; text: string } | null>(null);

    const seitenUrl = appDownloadSeitenUrl(window.location.origin, window.location.pathname);

    useEffect(() => {
        let aktiv = true;
        void (async () => {
            const { zugang } = await ladeDownloadZugang();
            // In der App kommt die Release-Info der eigenen Variante aus
            // useAppUpdate, auf iOS wird keine gebraucht.
            const releases = zugang === 'intern' && plattform === 'browser' ? await ladeAlleAppReleases() : KEINE_RELEASES;
            if (aktiv) setStand({ geladen: true, zugang, releases });
        })();
        return () => { aktiv = false; };
    }, [plattform]);

    const irgendeineVersion = !!(stand.releases.handy || stand.releases.tablet);

    // QR-Code nur fuer den Rechner: auf dem Android-Geraet selbst ist er sinnlos.
    const zeigeQr = plattform === 'browser' && !aufAndroidGeraet && stand.zugang === 'intern' && irgendeineVersion;
    useEffect(() => {
        if (!zeigeQr) return undefined;
        let aktiv = true;
        QRCode.toDataURL(seitenUrl, { width: 480, margin: 2 })
            .then((dataUrl) => { if (aktiv) setQr(dataUrl); })
            .catch((e: unknown) => { console.warn('QR-Code konnte nicht erzeugt werden', e); });
        return () => { aktiv = false; };
    }, [zeigeQr, seitenUrl]);

    const herunterladen = async (variante: AppVariante) => {
        const release = stand.releases[variante];
        if (!release || laedtVariante) return;
        setLaedtVariante(variante);
        setFehler(null);
        const url = await apkDownloadLink(release);
        setLaedtVariante(null);
        if (!url) {
            setFehler({ variante, text: 'Der Download-Link ließ sich nicht erzeugen. Bitte gleich noch einmal versuchen.' });
            return;
        }
        apkHerunterladen(url, release.file);
    };

    let inhalt: React.ReactNode;
    if (!stand.geladen) {
        inhalt = (
            <Karte>
                <p className="text-sm text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Wird geladen …
                </p>
            </Karte>
        );
    } else if (stand.zugang === 'abgemeldet') {
        inhalt = (
            <Karte titel="Bitte anmelden" symbol={<Lock className="w-4 h-4 text-slate-500" />}>
                <p className="text-sm text-slate-600">Die App steht nach der Anmeldung zum Herunterladen bereit.</p>
            </Karte>
        );
    } else if (stand.zugang === 'freelancer') {
        inhalt = (
            <Karte titel="Nicht verfügbar" symbol={<Lock className="w-4 h-4 text-slate-500" />}>
                <p className="text-sm text-slate-600 leading-relaxed">
                    Die Android-App ist für Ihr Konto nicht verfügbar. Die Web-App funktioniert im Browser wie gewohnt.
                </p>
            </Karte>
        );
    } else if (plattform === 'app') {
        inhalt = (
            <>
                <InstalliertKarte update={update} variante={eigeneVariante} />
                {/* Anmeldung mit Fingerabdruck/Gesicht fuer dieses Geraet (Plugin Biometrie, ab 1.0.3). */}
                <BiometrieKarte />
                {/* Schalter der Kamera-Einblendung fuer dieses Geraet (immer abschaltbar). */}
                <VerortungGeraetKarte />
                {/* Messpilot Sensor-Verortung (docs/maengel-verortung-plan.md 9.4, P5) — nur in der App. */}
                <SensorMessmodus />
            </>
        );
    } else if (plattform === 'ios') {
        inhalt = <IosKarte />;
    } else if (!irgendeineVersion) {
        inhalt = (
            <Karte titel="Noch keine App-Version veröffentlicht" symbol={<Info className="w-4 h-4 text-slate-500" />}>
                <p className="text-sm text-slate-600 leading-relaxed">
                    Sobald eine Version bereitsteht, finden Sie sie hier. Bis dahin funktioniert alles wie gewohnt im Browser.
                </p>
            </Karte>
        );
    } else {
        const reihenfolge: AppVariante[] = vorschlag === 'tablet' ? ['tablet', 'handy'] : ['handy', 'tablet'];
        const veroeffentlicht = reihenfolge.filter((v) => stand.releases[v] !== null);
        inhalt = (
            <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {reihenfolge.map((v) => (
                        <VariantenKarte
                            key={v}
                            variante={v}
                            release={stand.releases[v]}
                            empfohlen={vorschlag === v}
                            laedt={laedtVariante === v}
                            fehler={fehler?.variante === v ? fehler.text : null}
                            onHerunterladen={() => { void herunterladen(v); }}
                        />
                    ))}
                </div>
                <p className="text-xs text-slate-500 px-1">
                    Beide Apps lassen sich nebeneinander installieren. App schon installiert? Einfach öffnen, sie meldet
                    Updates selbst.
                </p>

                <Karte titel="Installation in vier Schritten" symbol={<Download className="w-4 h-4 text-emerald-600" />}>
                    <ol className="space-y-3">
                        <Schritt nr={1} titel="Herunterladen">
                            Auf dem Android-Gerät die passende App herunterladen. Fragt Chrome „Datei trotzdem
                            herunterladen?“, mit „Herunterladen“ bestätigen.
                        </Schritt>
                        <Schritt nr={2} titel="Unbekannte Apps zulassen (einmalig)">
                            Beim ersten Mal meldet Android, dass die Installation aus dieser Quelle nicht zulässig ist.
                            „Einstellungen“ tippen, „Dieser Quelle vertrauen“ bzw. „Unbekannte Apps zulassen“ für Chrome
                            einschalten und zurückgehen.
                        </Schritt>
                        <Schritt nr={3} titel="Installieren">
                            Die heruntergeladene Datei öffnen (Benachrichtigung „Download abgeschlossen“ oder App „Dateien“
                            → Downloads) und „Installieren“ tippen.
                        </Schritt>
                        <Schritt nr={4} titel="Einmal anmelden">
                            Die App öffnen („{VARIANTEN_TEXT.handy.appName}“ bzw. „{VARIANTEN_TEXT.tablet.appName}“) und
                            mit den gewohnten Zugangsdaten anmelden. Die Anmeldung im Browser gilt in der App nicht —
                            einmal neu anmelden genügt.
                        </Schritt>
                    </ol>
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <p className="font-semibold flex items-start gap-2">
                            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /> Warnung von Google Play Protect
                        </p>
                        <p className="mt-1 leading-relaxed">
                            Weil die App nicht aus dem Play Store kommt, kann Play Protect warnen („Unbekannte App“ oder
                            „App wurde nicht geprüft“). Das ist bei dieser Verteilung normal: „Details“ bzw. „Weitere Details“
                            tippen, dann „Trotzdem installieren“. Im Zweifel die Prüfsummen unten mit der IT abgleichen.
                        </p>
                    </div>
                </Karte>

                <Karte titel="Prüfsummen (für die IT)" symbol={<Fingerprint className="w-4 h-4 text-slate-600" />}>
                    <dl className="space-y-3 text-sm">
                        {veroeffentlicht.map((v) => {
                            const r = stand.releases[v]!;
                            return (
                                <React.Fragment key={v}>
                                    <div>
                                        <dt className="text-slate-500">{VARIANTEN_TEXT[v].titel} — SHA-256 der Datei {r.file}</dt>
                                        <dd className="mt-0.5 font-mono text-xs text-slate-800 break-all select-all">{r.sha256}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-500">{VARIANTEN_TEXT[v].titel} — Fingerabdruck des Signaturzertifikats (SHA-256)</dt>
                                        <dd className="mt-0.5 font-mono text-xs text-slate-800 break-all select-all">{alsFingerabdruck(r.certSha256)}</dd>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </dl>
                    <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                        Jede Version ist mit demselben Zertifikat signiert. Nur dann installiert Android ein Update über die
                        vorhandene App.
                    </p>
                </Karte>

                {zeigeQr && (
                    <Karte titel="Auf dem Handy oder Tablet öffnen" symbol={<QrCode className="w-4 h-4 text-slate-600" />}>
                        <div className="flex flex-col sm:flex-row items-center gap-4">
                            {qr
                                ? <img src={qr} alt="QR-Code zur Download-Seite" className="w-44 h-44 rounded-lg border border-slate-200" />
                                : <div className="w-44 h-44 rounded-lg border border-slate-200 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
                            <div className="text-sm text-slate-600 leading-relaxed min-w-0">
                                <p>
                                    Mit der Kamera des Android-Geräts scannen, im Browser anmelden, dann herunterladen. Dort
                                    steht die passende Variante vorn.
                                </p>
                                <p className="mt-2 font-mono text-xs text-slate-500 break-all">{seitenUrl}</p>
                            </div>
                        </div>
                    </Karte>
                )}
            </>
        );
    }

    return (
        <div data-glass-view="app-seite" className="h-full overflow-y-auto animate-fade-in">
            <div className="max-w-3xl mx-auto p-4 md:p-6 pb-24 space-y-4">
                <header data-glass="kopf" className="flex items-center gap-3">
                    {onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            aria-label="Zurück"
                            className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 text-slate-600 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                    )}
                    <div className="p-2 rounded-xl bg-emerald-50 border border-emerald-100">
                        <Smartphone className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-xl font-bold text-slate-800">{plattform === 'app' ? 'App-Info' : 'App für Android'}</h1>
                        <p className="text-xs text-slate-500">Zusätzlich zur Web-App, freiwillig. Die Web-App bleibt vollständig nutzbar.</p>
                    </div>
                </header>
                {inhalt}
            </div>
        </div>
    );
}
